// Транспорт раздела «Отгрузка» — ручки ядра /ut/*. ТОЛЬКО ЧТЕНИЕ.
//
// Тот же порядок, что в api/chain.ts и api/products.ts: обычные GET-ручки ядра, общий разбор
// ошибок, никакой записи. Записи здесь не будет никогда: создание заказа и Реализация — это
// запись в 1С, а у прокси ядра её нет вовсе (наверх идёт одна функция и только GET).
//
// ЧЕМ ЭТО ОТЛИЧАЕТСЯ ОТ api/purchasing.ts, КОТОРЫЙ ЭКРАН ИСПОЛЬЗОВАЛ РАНЬШЕ.
// Там документы едут из 1С через прокси: до 500 заказов и до 500 реализаций на каждое
// открытие, а «ждёт отгрузки», суммы и поиск считаются в браузере по загруженному куску.
// Здесь один запрос к Postgres, и считает Postgres. Хуки purchasing никуда не делись и
// остаются рабочими: на них по-прежнему живёт «Кабинет закупщика» и справочники НАЗВАНИЙ
// (склады, менеджеры, контрагенты) — их в зеркале документов нет, там только ссылки.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import { БАЗА, ПУТИ, РАЗМЕР_СТРАНИЦЫ } from '../lib/ut'
import type { ОтгрузкаОтвет, ФильтрыОтгрузки } from '../lib/ut'

/** Тот же разбор ответа, что в api/chain.ts, api/p73.ts и api/products.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 — «такой запрос обслужить нельзя». Пустая таблица здесь читалась бы как
    // «отгрузок нет», а на этом экране это самая дорогая ложь из возможных.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    // 404 — ручки в ядре ещё нет (не добавлены две строки в core_api.py или маршрут в BFF).
    // Отдельным типом ошибки, чтобы экран сказал это словами, а не «что-то пошло не так».
    //
    // Путь берётся ИЗ ЗАПРОСА, а не зашит строкой: ручек в модуле теперь две (`/shipping`
    // и `/docs`), и зашитое имя врало бы про ту, которой на самом деле нет. Так уже было:
    // блок документов 1С сообщал «ядро не знает ручку /ut/shipping», хотя спрашивал /docs.
    if (res.status === 404) {
      const имя = '/ut' + путь.split('?')[0]
      throw new CoreUnsupported(
        `ядро не знает ручку ${имя}: не подключён модуль ut_api или маршрут BFF`,
      )
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

/** Отказ ядра и потерянную сессию не повторяем: ответ детерминированный. */
const общее = {
  retry: (попытка: number, ошибка: unknown) =>
    !(ошибка instanceof CoreUnsupported) && !(ошибка instanceof Unauthorized) && попытка < 1,
} as const

/**
 * Страница отгрузки со всеми итогами. Один запрос на весь экран.
 *
 * `placeholderData` оставляет предыдущий ответ на месте, пока едет следующий: иначе при
 * смене фильтра или страницы таблица моргает пустотой, а пустота здесь читается как
 * «документов нет».
 *
 * `staleTime` — минута. Зеркало обновляет синк раз в несколько минут, и перезапрашивать
 * его на каждый клик незачем; кнопка «обновить» всё равно ходит в ядро принудительно.
 */
export const useОтгрузка = (ф: ФильтрыОтгрузки = {}) =>
  useQuery({
    queryKey: ['ut', 'shipping', ф],
    queryFn: () => запрос<ОтгрузкаОтвет>(ПУТИ.отгрузка({ limit: РАЗМЕР_СТРАНИЦЫ, ...ф })),
    placeholderData: (prev: ОтгрузкаОтвет | undefined) => prev,
    staleTime: 60_000,
    ...общее,
  })

// ─────────────────── документы 1С по компании Bitrix (`/ut/docs`) ───────────────────
//
// ЗАЧЕМ ОТДЕЛЬНАЯ ФОРМА ОТВЕТА, А НЕ `ОтгрузкаОтвет`. Строка `/ut/docs` — это ШАПКА
// документа как она лежит в зеркале (`ref_key`, `doc_date`, `sum_doc`, `contragent_ref`),
// а `/ut/shipping` отдаёт разобранную строку раздела «Отгрузка» (`ref`, `date`, `sum`,
// `client_ref`, «ждёт отгрузки», способ доставки). Это разные ручки с разными полями, и
// один тип на обе однажды дал бы пустую колонку вместо суммы. Типы поэтому здесь, а не в
// lib/ut.ts: тот файл обслуживает раздел «Отгрузка» и общий с ним.
//
// СВЯЗЬ КОСВЕННАЯ, И ЭТО ГЛАВНОЕ, ЧТО НАДО ЗНАТЬ ПРО ЭТУ РУЧКУ. Прямой ссылки «компания
// Bitrix → контрагент 1С» в зеркале НЕТ ВОВСЕ. Цепочка идёт через сделку: номер сделки
// Bitrix лежит в Description справочника сделок 1С и разобран при заливке. Ядро говорит это
// словами в `data_notes.link_is_indirect`, и экран обязан их показать, а не пересказать.

/** Шапка документа 1С из зеркала. Ссылки, а не имена: названий в зеркале нет. */
export interface ДокументКлиента {
  kind: string
  ref_key: string
  number: string | null
  doc_date: string | null
  status: string | null
  posted: boolean
  sum_doc: number | null
  currency_ref: string | null
  plan_date: string | null
  warehouse_ref: string | null
  contragent_ref: string | null
  partner_ref: string | null
  manager_ref: string | null
  deal_ref: string | null
  /** Номер сделки Bitrix. null — в справочнике сделок 1С там не номер, а текст. */
  deal_id: number | null
  deal_title: string | null
  deal_stage: string | null
  deal_assigned_by_id: number | null
  company_id: number | null
}

/** Итог по виду документа. Считает ядро одним SQL по ВСЕЙ выборке, а не по странице. */
export interface ИтогПоВиду {
  kind: string
  n: number
  n_posted: number
  sum_doc: number | null
  deals: number
  first_doc: string | null
  last_doc: string | null
}

/** Контрагент 1С, на которого оформлены документы клиента. У одного клиента их до 13. */
export interface КонтрагентКлиента {
  ref: string
  n: number
  deals: number
  sum_doc: number | null
}

export interface ДокументыКлиента {
  rows: ДокументКлиента[]
  total: number
  next: number | null
  limit: number
  start: number
  totals: { count: number; by_kind: ИтогПоВиду[] }
  contragents: КонтрагентКлиента[]
  /** Сколько сделок клиента вообще и у скольких из них нашлись документы 1С. */
  coverage: { deals_total: number; deals_with_docs: number }
  /**
   * Граница самого зеркала. `docs_no_deal` — документы, не привязанные НИ К КАКОЙ компании:
   * на 28.07.2026 это 651 из 2 983. Из-за них «документов нет» ≠ «отгрузки не было».
   */
  mirror_scope: { docs_total: number; docs_with_deal: number; docs_no_deal: number }
  filters: { company: number | null; deals: number[] | null; kinds: string[] | null
             days: number | null }
  mirror: { synced: { entity: string; last_sync_at: string | null; last_count: number | null
                      age_min: number | null }[]
            age_min: number | null }
  data_notes: Record<string, string>
}

export interface ФильтрыДокументов {
  /** id компании Bitrix. Ядро само пройдёт по её сделкам — своего перебора здесь нет. */
  company?: string | number
  /** Номера сделок Bitrix списком. Второй способ спросить то же самое. */
  deal?: string
  kind?: string
  days?: number
  start?: number
  limit?: number
}

/**
 * Документы 1С по клиенту. Один запрос на весь блок: и строки, и итоги по видам, и список
 * контрагентов, и покрытие сделок ядро считает ОДНИМ обращением.
 *
 * `enabled` — не украшение: блок закрыт правами раздела «Отгрузка», и у роли без него
 * запрос вернул бы 403 на каждое открытие карточки клиента. Дверь ставит экран, ходить
 * за заведомым отказом незачем.
 */
export const useДокументыКлиента = (ф: ФильтрыДокументов, включён = true) =>
  useQuery({
    queryKey: ['ut', 'docs', ф],
    queryFn: () => {
      const p = new URLSearchParams()
      for (const [k, v] of Object.entries(ф)) {
        if (v === undefined || v === null || v === '') continue
        p.set(k, String(v))
      }
      const s = p.toString()
      return запрос<ДокументыКлиента>(`/docs${s ? `?${s}` : ''}`)
    },
    enabled: включён,
    staleTime: 60_000,
    ...общее,
  })
